import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
	listContactTaxonomyHousesHouseIdContactsTaxonomyGetOptions,
	listContactTaxonomyHousesHouseIdContactsTaxonomyGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import {
	ProfessionalContacts,
	type CreateContactRoleRequest,
	type TaxonomyCategoryOut,
	type TaxonomyRoleOut,
} from '@/client';
import { toaster } from '@/components/ui/toaster';

/** The house's custom role with this label, compared the way the server does: trimmed, any case. */
const findCustomRoleByLabel = (taxonomy: readonly TaxonomyCategoryOut[], label: string) => {
	const wanted = label.trim().toLocaleLowerCase();
	return taxonomy
		.flatMap((category) => category.roles ?? [])
		.find((role) => role.is_custom && role.label.trim().toLocaleLowerCase() === wanted);
};

/** The catalog with the role added to its group, unless it is already there. */
const withRole = (taxonomy: TaxonomyCategoryOut[], categoryCode: string, role: TaxonomyRoleOut) =>
	taxonomy.map((category) =>
		category.code !== categoryCode || category.roles?.some((existing) => existing.code === role.code)
			? category
			: { ...category, roles: [...(category.roles ?? []), role] },
	);

/**
 * Creates a custom role for this property and resolves to it.
 *
 * The server generates the role's `code`, so the caller never sends one. A 409 means the house
 * already has a custom role with that label — a second tab can win the race — and resolves to that
 * role from a fresh catalog instead of failing. It is written into the cached catalog before the
 * refetch, so a picker showing it by code has its label at once.
 */
export const useCreateContactRole = (houseId: number) => {
	const queryClient = useQueryClient();
	const taxonomyOptions = listContactTaxonomyHousesHouseIdContactsTaxonomyGetOptions({ path: { house_id: houseId } });
	const taxonomyKey = listContactTaxonomyHousesHouseIdContactsTaxonomyGetQueryKey({ path: { house_id: houseId } });

	return useMutation({
		mutationFn: async (body: CreateContactRoleRequest): Promise<TaxonomyRoleOut> => {
			try {
				const { data } = await ProfessionalContacts.createCustomContactRoleHousesHouseIdContactsRolesPost({
					path: { house_id: houseId },
					body,
					throwOnError: true,
				});
				return data;
			} catch (error) {
				if (!isAxiosError(error) || error.response?.status !== 409) throw error;
				const taxonomy = await queryClient.fetchQuery({ ...taxonomyOptions, staleTime: 0 });
				const existing = findCustomRoleByLabel(taxonomy, body.label);
				if (!existing) throw error;
				return existing;
			}
		},
		onSuccess: (role, { category_code }) => {
			queryClient.setQueryData<TaxonomyCategoryOut[]>(taxonomyKey, (taxonomy) =>
				taxonomy ? withRole(taxonomy, category_code, role) : taxonomy,
			);
			void queryClient.invalidateQueries({ queryKey: taxonomyKey });
		},
		onError: (error) => {
			toaster.create({ title: 'Could not create the service', description: error.message, type: 'error' });
		},
	});
};
